import {
  Box,
  Flex,
  HStack,
  Heading,
  Spinner,
  Table,
  Text,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  FiArrowLeft,
  FiChevronDown,
  FiChevronUp,
  FiSearch,
  FiX,
} from "react-icons/fi"

import { OpenAPI } from "@/client"
import { PaginationFooter } from "@/components/Common/PaginationFooter"
import { VisionButton, VisionCard, VisionInput } from "@/components/VisionUI"
import { Badge } from "@/components/ui/badge"
import { InputGroup } from "@/components/ui/input-group"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/admin-table-data")({
  component: AdminTableData,
  validateSearch: (search: Record<string, unknown>) => ({
    table: (search.table as string) || "",
  }),
})

function apiFetch(path: string) {
  const token = localStorage.getItem("access_token")
  const base = (OpenAPI.BASE || "").replace(/\/$/, "")
  return fetch(`${base}/api/v1/admin${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
}

function AdminTableData() {
  const sc = useSemanticColors()
  const { t } = useTranslation(["admin", "common"])
  const navigate = useNavigate()
  const { table: tableName } = useSearch({ from: "/_layout/admin-table-data" })

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const [appliedSearch, setAppliedSearch] = useState("")
  const [sortColumn, setSortColumn] = useState("")
  const [sortDesc, setSortDesc] = useState(true)
  const pageSize = 50

  const applyFilter = () => {
    setAppliedSearch(search)
    setPage(1)
  }

  const clearFilter = () => {
    setSearch("")
    setAppliedSearch("")
    setPage(1)
  }

  const toggleSort = (col: string) => {
    if (sortColumn === col) {
      setSortDesc(!sortDesc)
    } else {
      setSortColumn(col)
      setSortDesc(true)
    }
    setPage(1)
  }

  const { data: result, isLoading } = useQuery({
    queryKey: [
      "admin-table-data",
      tableName,
      page,
      pageSize,
      appliedSearch,
      sortColumn,
      sortDesc,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        skip: String((page - 1) * pageSize),
        limit: String(pageSize),
      })
      if (appliedSearch) params.set("search", appliedSearch)
      if (sortColumn) {
        params.set("sort_column", sortColumn)
        params.set("sort_desc", String(sortDesc))
      }
      const res = await apiFetch(`/database/tables/${tableName}/data?${params}`)
      if (!res.ok) return { columns: [], data: [], count: 0 }
      return res.json() as Promise<{
        columns: string[]
        data: Record<string, any>[]
        count: number
      }>
    },
    enabled: !!tableName,
  })

  const columns = result?.columns ?? []
  const rows = result?.data ?? []
  const totalCount = result?.count ?? 0

  if (!tableName) {
    return (
      <VisionCard>
        <Text color={sc.textMuted}>No table selected.</Text>
      </VisionCard>
    )
  }

  return (
    <Box>
      <Flex
        align={{ base: "flex-start", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap={{ base: 2, md: 4 }}
        mb={{ base: 5, md: 8 }}
      >
        <Box flex={1} minW={0} w="100%">
          <Heading
            size={{ base: "lg", md: "xl" }}
            color={sc.heading}
            fontFamily="mono"
            wordBreak="break-all"
          >
            {tableName}
          </Heading>
          <HStack gap={2} mt={1}>
            <Badge colorScheme="blue">{totalCount.toLocaleString()} rows</Badge>
          </HStack>
        </Box>
        <VisionButton
          variant="ghost"
          onClick={() => navigate({ to: "/admin-database" })}
          size="sm"
          alignSelf={{ base: "flex-end", md: "auto" }}
        >
          <FiArrowLeft />
          {t("common:actions.back")}
        </VisionButton>
      </Flex>

      <VisionCard p={{ base: 3, md: "22px" }}>
        <Flex
          mb={4}
          gap={2}
          direction={{ base: "column", sm: "row" }}
          align={{ base: "stretch", sm: "center" }}
        >
          <InputGroup
            startElement={<FiSearch color={sc.iconMuted} />}
            flex={1}
            maxW={{ base: "100%", sm: "500px" }}
          >
            <VisionInput
              placeholder={t("admin:database.searchData")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyFilter()
              }}
            />
          </InputGroup>
          <Flex gap={2} wrap="wrap">
            <VisionButton variant="brand" onClick={applyFilter} size="sm">
              {t("common:actions.filter")}
            </VisionButton>
            {appliedSearch && (
              <VisionButton variant="ghost" onClick={clearFilter} size="sm">
                <FiX /> {t("common:actions.clearFilter")}
              </VisionButton>
            )}
          </Flex>
        </Flex>

        {isLoading ? (
          <Flex justify="center" py={8}>
            <Spinner color={sc.spinnerColor} />
          </Flex>
        ) : rows.length === 0 ? (
          <Text color={sc.textMuted} py={4}>
            {appliedSearch
              ? t("admin:database.noResults")
              : t("admin:database.emptyTable")}
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  {columns.map((col) => (
                    <Table.ColumnHeader
                      key={col}
                      color={sc.textMuted}
                      cursor="pointer"
                      onClick={() => toggleSort(col)}
                      _hover={{ color: sc.heading }}
                      whiteSpace="nowrap"
                    >
                      <HStack gap={1}>
                        <Text>{col}</Text>
                        {sortColumn === col &&
                          (sortDesc ? (
                            <FiChevronDown size={12} />
                          ) : (
                            <FiChevronUp size={12} />
                          ))}
                      </HStack>
                    </Table.ColumnHeader>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map((row, rowIdx) => (
                  <Table.Row key={rowIdx}>
                    {columns.map((col) => (
                      <Table.Cell
                        key={col}
                        fontSize="xs"
                        fontFamily="mono"
                        color={sc.textSecondary}
                        maxW="300px"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        title={String(row[col] ?? "")}
                      >
                        {row[col] === null ? (
                          <Text as="span" color={sc.textSubtle}>
                            NULL
                          </Text>
                        ) : typeof row[col] === "object" ? (
                          JSON.stringify(row[col])
                        ) : (
                          String(row[col])
                        )}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}

        {totalCount > pageSize && (
          <PaginationFooter
            page={page}
            count={totalCount}
            pageSize={pageSize}
            setPage={setPage}
          />
        )}
      </VisionCard>
    </Box>
  )
}
